import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useData } from '../../context/DataContext';

import BusinessIcon from '@mui/icons-material/Business';
import JobApplicationForm from './JobApplicationForm';
import DropYourResumeForm from './DropYourResumeForm';
import { useMarkPageReadyWithoutHero } from '../../context/PageReadyContext';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const Careers = () => {
  useMarkPageReadyWithoutHero();
  const { state } = useData();
  const { jobs } = state;

  const [filters, setFilters] = useState({
    department: ''
  });

  const [selectedJob, setSelectedJob] = useState(null);
  const [applicationFormOpen, setApplicationFormOpen] = useState(false);
  const [dropResumeFormOpen, setDropResumeFormOpen] = useState(false);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyNow = (job) => {
    setSelectedJob(job);
    setApplicationFormOpen(true);
  };

  const handleCloseApplicationForm = () => {
    setApplicationFormOpen(false);
    setSelectedJob(null);
  };

  const filteredJobs = jobs.filter(job => {
    if (filters.department && job.department !== filters.department) return false;
    return job.isActive;
  });

  const departments = [...new Set(jobs.map(job => job.department))];

  return (
    <Box>
      {/* Hero Section */}
      <Paper sx={{
        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
        color: 'white',
        py: 8,
        textAlign: 'center',
        MozBorderRadiusTopleft:'0'
      }}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Join Our Team
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9 }}>
            Build your career with us and make a difference in healthcare
          </Typography>
        </Container>
      </Paper>

      {/* Job Filters */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 4 }}>
            Find Your Perfect Position
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={filters.department}
                  label="Department"
                  onChange={(e) => handleFilterChange('department', e.target.value)}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Job Listings */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 6 }}>
          Current Openings ({filteredJobs.length})
        </Typography>
        
        {filteredJobs.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No positions match your current filters. Please try different criteria.
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={4}>
            {filteredJobs.map((job) => (
              <Grid item xs={12} key={job.id}>
                <Card sx={{ '&:hover': { boxShadow: 4 } }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" component="h3" gutterBottom>
                          {job.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                          <Chip 
                            icon={<BusinessIcon />} 
                            label={job.department} 
                            color="primary" 
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                      {job.showPay && (
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                          PKR {job.salary}
                        </Typography>
                      )}
                    </Box>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      paragraph
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(job.description) }}
                    />
                    
                    <Typography variant="body2" color="text.secondary" paragraph>
                      <strong>Requirements:</strong>
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      paragraph
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(job.requirements) }}
                    />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Posted: {new Date(job.postedDate).toLocaleDateString()}
                      </Typography>
                      <Button 
                        variant="contained" 
                        size="small"
                        onClick={() => handleApplyNow(job)}
                      >
                        Apply Now
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Drop Your Resume Section */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Don't See a Position That Fits?
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              Drop your resume and we'll keep you in mind for future opportunities
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => setDropResumeFormOpen(true)}
              sx={{ mt: 2, px: 4, py: 1.5 }}
            >
              Drop Your Resume
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Job Application Form Modal */}
      <JobApplicationForm
        open={applicationFormOpen}
        onClose={handleCloseApplicationForm}
        job={selectedJob}
      />

      {/* Drop Your Resume Form Modal */}
      <DropYourResumeForm
        open={dropResumeFormOpen}
        onClose={() => setDropResumeFormOpen(false)}
      />
    </Box>
  );
};

export default Careers; 